import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useStore } from "react-redux";

import type { AppState } from "src/store";

import { DataLoaderManager } from "./DataLoaderManager";

export type SalesTransactionsDataLoaderContextValue = {
  /** Single shared manager instance; null before mount / after dispose. */
  getManager: () => DataLoaderManager | null;
};

const defaultValue: SalesTransactionsDataLoaderContextValue = {
  getManager: () => null,
};

const SalesTransactionsDataLoaderContext =
  createContext<SalesTransactionsDataLoaderContextValue>(defaultValue);

export function SalesTransactionsDataLoaderProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const store = useStore<AppState>();
  const managerRef = useRef<DataLoaderManager | null>(null);

  useEffect(() => {
    managerRef.current = new DataLoaderManager(store);
    return () => {
      managerRef.current?.dispose();
      managerRef.current = null;
    };
  }, [store]);

  const value = useMemo<SalesTransactionsDataLoaderContextValue>(
    () => ({
      getManager: () => managerRef.current,
    }),
    []
  );

  return (
    <SalesTransactionsDataLoaderContext.Provider value={value}>
      {children}
    </SalesTransactionsDataLoaderContext.Provider>
  );
}

export function useSalesTransactionsDataLoaderContext(): SalesTransactionsDataLoaderContextValue {
  return useContext(SalesTransactionsDataLoaderContext);
}
